import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Message } from 'primereact/message'
import { addHours } from 'date-fns'

import useText from '../../../../../common/hooks/useText'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { isFetchBaseQueryError } from '../../../../../common/redux/api/fetchBaseQueryWithReauth'
import {
    type AddServicePartnerRequest,
    type ServicePartnerConnectionResponse,
    useCompanyAddServicePartnerMutation,
    useCompanyGetServicePartnerCodeInformationMutation,
} from '../../../../../common/redux/api/web-api'

export type AddServicePartnerDialogProps = {
    visible: boolean
    companyId?: string
    onHide: () => void
    onSuccess: () => void
    servicePartners: ServicePartnerConnectionResponse[]
    partnerId?: number
}

type ServicePartnerLookupFormValues = {
    partnerReferenceCode: string
}

type AddServicePartnerFormValues = {
    validTo: Date | null
    indefiniteAccess: boolean
}

type LookupBlockingMessageKey = 'msgServicePartnerCurrentPartner' | 'msgServicePartnerAlreadyAdded'

const lookupFormId = 'service-partner-lookup-form'
const addFormId = 'add-service-partner-form'

const getLookupErrorMessageKey = (error: unknown): string => {
    if (!isFetchBaseQueryError(error)) {
        return 'msgServicePartnerCodeLookupFailed'
    }

    switch (error.status) {
        case 400:
            return 'msgServicePartnerCodeInvalid'
        case 409:
            return 'msgServicePartnerAlreadyAdded'
        case 410:
            return 'msgServicePartnerValidationTokenExpired'
        default:
            return 'msgServicePartnerCodeLookupFailed'
    }
}

const getAddErrorMessageKey = (error: unknown): string => {
    if (!isFetchBaseQueryError(error)) {
        return 'msgServicePartnerAddFailed'
    }

    switch (error.status) {
        case 409:
            return 'msgServicePartnerValidationTokenAlreadyUsed'
        case 410:
            return 'msgServicePartnerValidationTokenExpired'
        default:
            return 'msgServicePartnerAddFailed'
    }
}

const normalizePartnerName = (name?: string | null): string => name?.trim().toLocaleLowerCase() ?? ''

const getLookupBlockingMessageKey = ({
    matchedPartnerId,
    matchedPartnerName,
    partnerId,
    servicePartners,
}: {
    matchedPartnerId?: number
    matchedPartnerName?: string | null
    partnerId?: number
    servicePartners: ServicePartnerConnectionResponse[]
}): LookupBlockingMessageKey | undefined => {
    if (matchedPartnerId != null && partnerId != null && matchedPartnerId === partnerId) {
        return 'msgServicePartnerCurrentPartner'
    }

    const normalizedMatchedPartnerName = normalizePartnerName(matchedPartnerName)
    if (!normalizedMatchedPartnerName) return undefined

    return servicePartners.some(
        (servicePartner) => normalizePartnerName(servicePartner.partnerName) === normalizedMatchedPartnerName
    )
        ? 'msgServicePartnerAlreadyAdded'
        : undefined
}

export function AddServicePartnerDialog({
    visible,
    companyId,
    onHide,
    onSuccess,
    servicePartners,
    partnerId,
}: AddServicePartnerDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const minimumValidTo = new Date()
    const [
        lookupServicePartner,
        {
            data: matchedServicePartnerCode,
            error: lookupError,
            isLoading: isLookingUp,
            reset: resetMatchedServicePartnerCode,
        },
    ] = useCompanyGetServicePartnerCodeInformationMutation()
    const [addServicePartner, { isLoading: isAdding }] = useCompanyAddServicePartnerMutation()
    const lookupForm = useForm<ServicePartnerLookupFormValues>({
        defaultValues: { partnerReferenceCode: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })
    const addForm = useForm<AddServicePartnerFormValues>({
        defaultValues: {
            validTo: addHours(new Date(), 1),
            indefiniteAccess: false,
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })
    const indefiniteAccess = addForm.watch('indefiniteAccess')
    const partnerReferenceCode = lookupForm.watch('partnerReferenceCode') ?? ''
    const partnerReferenceCodeRegistration = lookupForm.register('partnerReferenceCode')

    const validationToken = matchedServicePartnerCode?.invitation?.validationToken
    const matchedPartnerId = matchedServicePartnerCode?.partner?.partnerId
    const matchedPartnerName = matchedServicePartnerCode?.partner?.partnerName
    const hasNoMatch = isFetchBaseQueryError(lookupError) && lookupError.status === 404
    const lookupBlockingMessageKey = getLookupBlockingMessageKey({
        matchedPartnerId,
        matchedPartnerName,
        partnerId,
        servicePartners,
    })
    const lookupBlockingMessage = lookupBlockingMessageKey ? text(lookupBlockingMessageKey) : undefined
    const searchDisabled =
        !companyId ||
        isLookingUp ||
        isAdding ||
        lookupForm.formState.isSubmitting ||
        partnerReferenceCode.trim().length !== 8

    const lookupPartner = async ({ partnerReferenceCode }: ServicePartnerLookupFormValues) => {
        const code = partnerReferenceCode?.trim() ?? ''
        if (!companyId || code.length !== 8) {
            return
        }

        resetMatchedServicePartnerCode()

        try {
            await lookupServicePartner({
                companyId,
                servicePartnerCodeLookupRequest: { code },
            }).unwrap()
            addForm.reset({ validTo: addHours(new Date(), 1), indefiniteAccess: false })
        } catch (error) {
            if (isFetchBaseQueryError(error) && error.status === 404) {
                return
            }

            const messageKey = getLookupErrorMessageKey(error)
            const message = text(messageKey)
            toast.notifyError(message)
        }
    }

    const confirmAdd = async ({ validTo, indefiniteAccess }: AddServicePartnerFormValues) => {
        if (
            !companyId ||
            !validationToken ||
            (!indefiniteAccess && (!(validTo instanceof Date) || validTo.getTime() <= Date.now())) ||
            lookupBlockingMessage
        ) {
            return
        }

        const addServicePartnerRequest: AddServicePartnerRequest = {
            validationToken,
            validTo: indefiniteAccess ? null : validTo?.toISOString() ?? null,
        }

        try {
            await addServicePartner({
                companyId,
                addServicePartnerRequest,
            }).unwrap()
            toast.notifySuccess(text('msgServicePartnerAdded'))
            onSuccess()
            onHide()
        } catch (error) {
            toast.notifyError(text(getAddErrorMessageKey(error)))
        }
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnAdd')}
                type="submit"
                form={addFormId}
                disabled={
                    !validationToken ||
                    !!lookupBlockingMessage ||
                    isLookingUp ||
                    isAdding ||
                    (addForm.formState.isSubmitted && !addForm.formState.isValid)
                }
                loading={isAdding}
                data-testid="confirm-add-service-partner"
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblAddServicePartner')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="add-service-partner-dialog"
        >
            <form
                id={lookupFormId}
                className="flex flex-column gap-3"
                onSubmit={lookupForm.handleSubmit(lookupPartner)}
                noValidate
            >
                <div className="flex flex-column">
                    <label className="p-label required-label" htmlFor="service-partner-reference-code">
                        {text('lblServicePartnerReferenceCode')}
                    </label>
                    <div className="flex flex-column md:flex-row md:align-items-start gap-3">
                        <Input
                            id="service-partner-reference-code"
                            data-testid="service-partner-reference-code"
                            {...partnerReferenceCodeRegistration}
                            onChange={(event) => {
                                lookupForm.setValue('partnerReferenceCode', event.target.value.toUpperCase(), {
                                    shouldDirty: true,
                                })
                                resetMatchedServicePartnerCode()
                            }}
                            disabled={isLookingUp || isAdding}
                            className="flex-1"
                            placeholder="ABCDEFGH"
                            maxLength={8}
                            required
                            autoFocus
                        />

                        <Button
                            type="submit"
                            label={text('lblSearch')}
                            icon="pi pi-search"
                            loading={isLookingUp}
                            disabled={searchDisabled}
                            className="align-self-start"
                            data-testid="search-service-partner"
                        />
                    </div>
                </div>
            </form>

            {matchedServicePartnerCode?.partner || hasNoMatch ? (
                <form
                    id={addFormId}
                    className="flex flex-column gap-3 mt-4"
                    onSubmit={addForm.handleSubmit(confirmAdd)}
                    noValidate
                >
                    <div
                        className="border-1 surface-border border-round p-3"
                        data-testid={hasNoMatch ? 'service-partner-no-match' : 'service-partner-match'}
                    >
                        <div className="text-sm text-color-secondary mb-2">{text('lblMatchedServicePartner')}</div>
                        <div className="flex align-items-center justify-content-between gap-3">
                            <div className={hasNoMatch ? 'text-color-secondary' : 'font-bold'}>
                                {hasNoMatch
                                    ? text('msgServicePartnerCodeNotFound')
                                    : matchedPartnerName || text('lblServicePartnerUnknownPartner')}
                            </div>
                            {lookupBlockingMessage ? (
                                <div className="text-sm text-color-secondary text-right">{lookupBlockingMessage}</div>
                            ) : null}
                        </div>
                    </div>

                    {matchedServicePartnerCode?.partner && !lookupBlockingMessage ? (
                        <div className="flex flex-column gap-3">
                            <Controller
                                name="validTo"
                                control={addForm.control}
                                rules={{
                                    validate: (value) => {
                                        if (addForm.getValues('indefiniteAccess')) return true
                                        if (!(value instanceof Date)) {
                                            return text('msgServicePartnerAccessValidToRequired')
                                        }

                                        return (
                                            value.getTime() > Date.now() ||
                                            text('msgServicePartnerAccessValidToMustBeFuture')
                                        )
                                    },
                                }}
                                render={({ field }) => (
                                    <div className="flex flex-column">
                                        <label
                                            className={indefiniteAccess ? 'p-label' : 'p-label required-label'}
                                            htmlFor="service-partner-valid-to"
                                        >
                                            {text('lblServicePartnerAccessValidTo')}
                                        </label>
                                        <Calendar
                                            inputId="service-partner-valid-to"
                                            data-testid="service-partner-valid-to"
                                            value={field.value}
                                            onChange={(event) =>
                                                field.onChange(event.value instanceof Date ? event.value : null)
                                            }
                                            onBlur={field.onBlur}
                                            minDate={minimumValidTo}
                                            showTime
                                            hourFormat="24"
                                            showIcon
                                            showButtonBar
                                            showOnFocus={false}
                                            dateFormat="yy-mm-dd"
                                            className="w-full"
                                            invalid={!!addForm.formState.errors.validTo}
                                            disabled={isAdding || indefiniteAccess}
                                            required={!indefiniteAccess}
                                        />
                                        {addForm.formState.errors.validTo?.message ? (
                                            <Message
                                                severity="error"
                                                text={addForm.formState.errors.validTo.message}
                                                data-testid="service-partner-valid-to-error-message"
                                                className="mt-2"
                                            />
                                        ) : null}
                                    </div>
                                )}
                            />

                            <Controller
                                name="indefiniteAccess"
                                control={addForm.control}
                                render={({ field }) => (
                                    <div className="flex align-items-center gap-2">
                                        <Checkbox
                                            inputId="service-partner-indefinite-access"
                                            checked={field.value}
                                            onChange={(event) => {
                                                field.onChange(Boolean(event.checked))
                                                if (addForm.formState.isSubmitted) {
                                                    void addForm.trigger('validTo')
                                                }
                                            }}
                                        />
                                        <label
                                            htmlFor="service-partner-indefinite-access"
                                            className="cursor-pointer select-none"
                                        >
                                            {text('lblServicePartnerIndefiniteAccess')}
                                        </label>
                                    </div>
                                )}
                            />
                        </div>
                    ) : null}
                </form>
            ) : null}
        </Dialog>
    )
}
